import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { IconCheck, IconClose, IconWarning } from '../icons.js'

/**
 * Исход действия: получилось или нет. Третьего тона нет намеренно —
 * «предупреждение» на всплывающем сообщении означало бы, что человек должен
 * что-то с ним сделать, а сделать с исчезающей плашкой нельзя ничего.
 */
export type ToastTone = 'ok' | 'danger'

export interface Toaster {
  /**
   * Действие выполнено. Формулировка — тем же глаголом, что на кнопке, и в
   * том же лице: нажали «Удалить» — «Сценарий удалён». Не «Готово».
   */
  succeeded(message: string): void
  /** Действие не выполнено. Сообщение — причина от службы, как она пришла. */
  failed(message: string): void
}

interface Toast {
  id: number
  message: string
  tone: ToastTone
}

/**
 * Сколько живёт сообщение об успехе. Пять секунд — это «успел прочитать
 * шесть слов и вернулся к работе»: подтверждение читают боковым зрением, а
 * не изучают.
 */
const SUCCESS_LIFETIME_MS = 5000

/**
 * Молчаливая заглушка для тех, кто отрисован вне оболочки портала — в
 * тестах отдельного экрана. Тихо, а не с отказом: экран обязан оставаться
 * работоспособным сам по себе, а единственное место, где он живёт
 * по-настоящему (App), провайдер даёт всегда.
 */
const БЕЗ_УВЕДОМЛЕНИЙ: Toaster = {
  succeeded: () => undefined,
  failed: () => undefined,
}

const ToastContext = createContext<Toaster>(БЕЗ_УВЕДОМЛЕНИЙ)

export function useToaster(): Toaster {
  return useContext(ToastContext)
}

export interface ToastProviderProps {
  children: ReactNode
}

/**
 * Всплывающие уведомления портала: одна полка на всё приложение, поэтому
 * сообщения всегда появляются в одном и том же месте, с какого бы экрана их
 * ни отправили.
 */
export function ToastProvider(props: ToastProviderProps) {
  const [toasts, setToasts] = useState<readonly Toast[]>([])
  const nextId = useRef(1)
  // Таймеры гашения держим списком, чтобы снять их при размонтировании:
  // сработавший после ухода со страницы таймер трогал бы состояние
  // компонента, которого уже нет.
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())

  useEffect(() => {
    const running = timers.current
    return () => {
      for (const timer of running) clearTimeout(timer)
      running.clear()
    }
  }, [])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const push = useCallback(
    (message: string, tone: ToastTone) => {
      const id = nextId.current
      nextId.current += 1
      setToasts((current) => [...current, { id, message, tone }])

      // Гаснет само только подтверждение. Причину отказа человек читает
      // словами, иногда переписывает в задачу — отнимать её по таймеру
      // значит заставлять повторять действие ради текста ошибки.
      if (tone !== 'ok') return
      const timer = setTimeout(() => {
        timers.current.delete(timer)
        dismiss(id)
      }, SUCCESS_LIFETIME_MS)
      timers.current.add(timer)
    },
    [dismiss],
  )

  const toaster = useMemo<Toaster>(
    () => ({
      succeeded: (message: string) => push(message, 'ok'),
      failed: (message: string) => push(message, 'danger'),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={toaster}>
      {props.children}
      <ToastShelf toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

interface ToastShelfProps {
  toasts: readonly Toast[]
  onDismiss: (id: number) => void
}

/**
 * Полка уведомлений — сверху по центру рабочей области.
 *
 * Выбор места не эстетический, а вынужденный: в редакторе сценария заняты
 * все четыре края — слева палитра узлов, справа параметры выбранного узла,
 * снизу замечания валидатора и журнал прогона, сверху по краям панель
 * действий сценария. Верх по центру — единственная полоса, на которой в
 * портале никогда не стоит орган управления, и она же ближе всего к тому
 * месту, куда человек только что смотрел, нажимая кнопку в панели действий.
 *
 * Фокус не забирается: уведомление появляется само, а человек в этот момент
 * что-то делает, и увести у него курсор ввода — хуже, чем промолчать.
 * Программа чтения с экрана узнаёт о сообщении по роли: `status` для
 * обычных (прочитает, когда закончит текущую фразу), `alert` для отказов
 * (прервёт).
 */
function ToastShelf(props: ToastShelfProps) {
  const { toasts, onDismiss } = props
  if (toasts.length === 0) return null

  return (
    <div className="уведомления">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`уведомление уведомление--${toast.tone}`}
          role={toast.tone === 'ok' ? 'status' : 'alert'}
          onKeyDown={(event) => {
            // Esc закрывает то уведомление, внутри которого сейчас фокус:
            // добраться до отказа с клавиатуры можно табуляцией до его
            // кнопки закрытия, и оттуда Esc короче, чем ещё одно нажатие.
            if (event.key !== 'Escape') return
            event.stopPropagation()
            onDismiss(toast.id)
          }}
        >
          {toast.tone === 'ok' ? <IconCheck /> : <IconWarning />}
          <span className="уведомление__текст">{toast.message}</span>
          {/* Кнопка только у отказа: подтверждение уходит само, и крестик
              рядом с ним предлагал бы работу там, где её нет. */}
          {toast.tone !== 'ok' && (
            <button
              type="button"
              className="уведомление__закрыть"
              aria-label="Закрыть"
              onClick={() => onDismiss(toast.id)}
            >
              <IconClose />
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
